import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { CustomerClassSource, type CustomerClass } from "@the-real-insight/tri-model";

import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { authenticationAtom } from "../State";
import { useClassCrosswalkAPI, type ClassCrosswalkEntry } from "../settings/operator-obligations/data-model/classCrosswalkAPI";

/**
 * "Kundenklasse" of an asset: the customer's own class (e.g. Bosch trade + class code), picked from the
 * tenant's customer-class crosswalk. Classes whose DIN 276 node matches the asset's category come first —
 * one candidate is the class an export would assign, several mean someone has to choose. A choice made
 * here is the customer's (`customer`), which no automatic run overwrites. Shown only when the tenant has a
 * crosswalk.
 */

const NONE = "__none__";

const keyOf = (c: { tradeCode?: string; classCode: string }) => `${c.tradeCode ?? ""}|${c.classCode}`.toUpperCase();

/** Entries on exactly the asset's node first, then entries on its cost group (as the server's crosswalkCandidates). */
const candidatesFor = (entries: ClassCrosswalkEntry[], din276Code: string): ClassCrosswalkEntry[] => {
  const group = din276Code.slice(0, 3);
  const exact = entries.filter((entry) => entry.din276Code === din276Code);
  return [...exact, ...entries.filter((entry) => entry.din276Code === group && !exact.includes(entry))];
};

export default function CustomerClassField({
  din276Code,
  value,
  onChange,
}: {
  din276Code?: string;
  value?: CustomerClass | null;
  onChange: (customerClass: CustomerClass | null) => void;
}) {
  const { t } = useTranslation();
  const fieldId = useId();
  const [authentication] = useAtom(authenticationAtom) as any;
  const api = useClassCrosswalkAPI(authentication?.customer);
  const [entries, setEntries] = useState<ClassCrosswalkEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!authentication?.customer) return;
    api
      .getEntries()
      .then((loaded) => !cancelled && setEntries(loaded))
      .catch(() => !cancelled && setEntries([]));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer]);

  const candidates = useMemo(() => (din276Code ? candidatesFor(entries, din276Code) : []), [entries, din276Code]);
  const others = useMemo(() => entries.filter((entry) => !candidates.includes(entry)), [entries, candidates]);
  const selected = value ? entries.find((entry) => keyOf(entry) === keyOf(value) && entry.scheme === value.scheme) : undefined;

  if (!entries.length && !value) return null;

  const label = (entry: { tradeCode?: string; classCode: string; classLabel?: string; din276Code?: string }) =>
    [entry.tradeCode, entry.classCode].filter(Boolean).join(" · ") + (entry.classLabel ? ` — ${entry.classLabel}` : "") + (entry.din276Code ? ` (${entry.din276Code})` : "");

  const pick = (id: string) => {
    if (id === NONE) return onChange(null);
    const entry = entries.find((e) => e._id === id);
    if (!entry) return;
    onChange({
      scheme: entry.scheme,
      tradeCode: entry.tradeCode,
      classCode: entry.classCode,
      classLabel: entry.classLabel,
      source: CustomerClassSource.customer,
      setAt: new Date(),
    });
  };

  return (
    <div className="mt-3 flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={fieldId}>{t("assetPropertiesPanel.customerClass.label")}</Label>
      <Select value={selected?._id ?? NONE} onValueChange={pick}>
        <SelectTrigger id={fieldId}>
          <SelectValue placeholder={t("assetPropertiesPanel.customerClass.none")}>
            {selected ? label(selected) : value ? label(value) : t("assetPropertiesPanel.customerClass.none")}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>{t("assetPropertiesPanel.customerClass.none")}</SelectItem>
          {candidates.length ? (
            <SelectGroup>
              <SelectLabel>{t("assetPropertiesPanel.customerClass.candidates", { code: din276Code })}</SelectLabel>
              {candidates.map((entry) => (
                <SelectItem key={entry._id} value={entry._id}>
                  {label(entry)}
                </SelectItem>
              ))}
            </SelectGroup>
          ) : null}
          {others.length ? (
            <SelectGroup>
              <SelectLabel>{t("assetPropertiesPanel.customerClass.all")}</SelectLabel>
              {others.map((entry) => (
                <SelectItem key={entry._id} value={entry._id}>
                  {label(entry)}
                </SelectItem>
              ))}
            </SelectGroup>
          ) : null}
        </SelectContent>
      </Select>
      {value?.source ? (
        <span className="text-xs text-muted-foreground">{t(`assetPropertiesPanel.customerClass.source.${value.source}`)}</span>
      ) : candidates.length > 1 ? (
        <span className="text-xs text-muted-foreground">{t("assetPropertiesPanel.customerClass.chooseHint", { count: candidates.length })}</span>
      ) : null}
    </div>
  );
}
